import { useState, useEffect } from "react";
import { Card, Button, Row, Col, Modal, Badge, Form, InputGroup, Spinner } from "react-bootstrap";
import { listarMedicos, inactivarMedico, crearMedico, reactivarMedico} from "../../services/medicoService";
import { listarGastosMedicos } from "../../services/gastoMedicoService";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import ModalDetalleMedico from "../../components/Medicos Componentes/ModalDetalleMedico";
import ModalEditarMedico from "../../components/Medicos Componentes/ModalEditarMedico";
import PageHeader from "../../components/ui/PageHeader";

function ListadoMedicos() {
    const [medicos, setMedicos] = useState([]);
    const [filtro, setFiltro] = useState("");
    const [showDetalle, setShowDetalle] = useState(false);
    const [showForm, setShowForm] = useState(false);
    const [showEdit, setShowEdit] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showConfirm, setShowConfirm] = useState(false);
    const [accionConfirm, setAccionConfirm] = useState(null);
    const [medicoAConfirmar, setMedicoAConfirmar] = useState(null);
    const [medicoSeleccionado, setMedicoSeleccionado] = useState(null);
    const [medicoAEditar, setMedicoAEditar] = useState(null);
    const [verInactivos, setVerInactivos] = useState(false);

    const [nuevoMedico, setNuevoMedico] = useState({
        nombre: "",
        apellido: "",
        matricula: "",
        especialidad: "",
        telefono: "",
    });

    // 🔹 Cargar médicos desde backend
    const cargarMedicos = async (filtroTexto = "", soloActivos = !verInactivos) => {
        try {
            setLoading(true);

            // Cargar médicos y gastos en paralelo
            const [medicosData, gastosData] = await Promise.all([
                listarMedicos(soloActivos, filtroTexto),
                listarGastosMedicos()
            ]);

            const ordenados = medicosData.sort((a, b) => (a.activo === b.activo ? 0 : a.activo ? -1 : 1));

            const medicosConConsultas = ordenados.map((m) => {
                // Filtrar gastos para este médico
                const gastosDelMedico = gastosData.filter(g => g.idMedico === m.idMedico);

                return {
                    ...m,
                    color: m.activo ? "primary" : "secondary",
                    consultas: gastosDelMedico, // Usamos los gastos reales
                };
            });
            setMedicos(medicosConConsultas);
        } catch (err) {
            console.error(err);
            toast.error(" No se pudieron cargar los médicos.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargarMedicos();
    }, []);

    const handleBuscar = (e) => {
        const texto = e.target.value;
        setFiltro(texto);
        cargarMedicos(texto);
    };

    // 🔹 Abrir modal detalle
    const abrirModalDetalle = (medico) => {
        setMedicoSeleccionado(medico);
        setShowDetalle(true); // Abre el ModalDetalleMedico (MUI Dialog)
    };

    const cerrarModalDetalle = () => setShowDetalle(false);

    // 🚀 FUNCIÓN CLAVE MODIFICADA: Coordina el cierre del detalle y la apertura de edición
    const abrirModalEditar = (medico) => {
        // 1. CERRAR el modal de Detalle (MUI Dialog)
        setShowDetalle(false);

        // 2. Establecer el médico a editar
        setMedicoAEditar(medico);

        // 3. ABRIR el modal de Edición (React Bootstrap Modal)
        // Esto asegura que solo haya un modal activo, resolviendo el problema de z-index.
        setShowEdit(true);
    };

    const cerrarModalEditar = () => {
        setShowEdit(false);
        setMedicoAEditar(null); // Limpiar al cerrar
    };

    // 🔹 Dar de baja lógica
    const handleBaja = async (id) => {
        if (window.confirm("¿Deseas dar de baja este médico?")) {
            try {
                await inactivarMedico(id);
                toast.success("✅ Médico dado de baja correctamente");
                cargarMedicos(filtro);
            } catch (err) {
                console.error(err);
                toast.error("❌ No se pudo dar de baja al médico.");
            }
        }
    };

    const abrirModalConfirmacion = (medico, accion) => {
        setMedicoAConfirmar(medico);
        setAccionConfirm(accion);
        setShowConfirm(true);
    };

    const cerrarModalForm = () => {
        setShowForm(false);
        setNuevoMedico({
            nombre: "",
            apellido: "",
            matricula: "",
            especialidad: "",
            telefono: "",
        });
    };

    const confirmarAccion = async () => {
        if (!medicoAConfirmar || !accionConfirm) return;

        try {
            if (accionConfirm === "baja") {
                await inactivarMedico(medicoAConfirmar.idMedico);
                toast.success("✅ Médico dado de baja correctamente");
            } else if (accionConfirm === "reactivar") {
                await reactivarMedico(medicoAConfirmar.idMedico);
                toast.success("✅ Médico reactivado correctamente");
            }

            cargarMedicos(filtro);
        } catch (err) {
            console.error(err);
            toast.error("❌ No se pudo completar la acción.");
        } finally {
            setShowConfirm(false);
            setAccionConfirm(null);
            setMedicoAConfirmar(null);
        }
    };

    // 🔹 Crear nuevo médico
    const handleCrearMedico = async () => {
        if (!nuevoMedico.nombre.trim() || !nuevoMedico.apellido.trim()) {
            toast.warning("⚠️ Debe completar nombre y apellido.");
            return;
        }

        if (nuevoMedico.telefono && !/^\d{8,15}$/.test(nuevoMedico.telefono)) {
            toast.warning("⚠️ El teléfono debe contener solo números (8 a 15 dígitos).");
            return;
        }

        try {
            await crearMedico({
                ...nuevoMedico,
                telefono: nuevoMedico.telefono ? Number(nuevoMedico.telefono) : null,
                activo: true,
            });
            toast.success("🎉 Médico registrado con éxito");
            setShowForm(false);
            setNuevoMedico({ nombre: "", apellido: "", matricula: "", especialidad: "", telefono: "" });
            cargarMedicos();
        } catch (err) {
            console.error(err);
            toast.error("❌ No se pudo registrar el médico.");
        }
    };

    return (
        <div className="area-gastos">
            <div className="container-fluid mt-4">
                <PageHeader
                    variante="plano"
                    icono="people-fill"
                    titulo="Médicos"
                    descripcion="Gestión de profesionales médicos"
                    accion={
                        <div className="d-flex gap-2">
                            <Button
                                variant={verInactivos ? "secondary" : "outline-secondary"}
                                onClick={() => {
                                    const nuevoVerInactivos = !verInactivos;
                                    setVerInactivos(nuevoVerInactivos);
                                    cargarMedicos(filtro, !nuevoVerInactivos);
                                }}
                                style={{ borderRadius: '8px', padding: '0.6rem 1.2rem', fontWeight: '500' }}
                            >
                                <i className={`bi bi-${verInactivos ? 'check-circle' : 'eye-slash'} me-2`}></i>
                                {verInactivos ? "Ver Activos" : "Ver Inactivos"}
                            </Button>
                            <Button 
                                variant="primary" 
                                onClick={() => setShowForm(true)}
                                style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                            >
                                <i className="bi bi-plus-circle me-2"></i>Registrar Médico
                            </Button>
                        </div>
                    }
                />

            <Card className="shadow-sm mb-4" style={{ border: 'none', borderRadius: '12px' }}>
                <Card.Body style={{ padding: '1rem' }}>
                    <InputGroup>
                        <InputGroup.Text style={{ background: 'transparent', border: 'none' }}>
                            <i className="bi bi-search" style={{ fontSize: '1.2rem', color: '#6B7280' }}></i>
                        </InputGroup.Text>
                        <Form.Control 
                            placeholder="Buscar por nombre o apellido..." 
                            value={filtro} 
                            onChange={handleBuscar}
                            style={{ border: 'none', fontSize: '1rem' }}
                        />
                    </InputGroup>
                </Card.Body>
            </Card>

            {loading ? (
                <div className="text-center mt-5">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-2">Cargando médicos...</p>
                </div>
            ) : medicos.length === 0 ? (
                <p className="text-muted text-center mt-4">No se encontraron médicos.</p>
            ) : (
                <Row>
                    {medicos.map((m) => {
                        const total = m.consultas.reduce((acc, c) => acc + (parseFloat(c.monto) || 0), 0);
                        return (
                            <Col lg={4} md={6} key={m.idMedico}>
                                <Card
                                    className={`mb-4 shadow-sm ${!m.activo ? "opacity-75" : ""}`}
                                    style={{ 
                                        border: 'none', 
                                        borderRadius: '12px',
                                        transition: 'all 0.3s ease',
                                        cursor: 'pointer',
                                        height: '100%'
                                    }}
                                    onMouseEnter={(e) => {
                                        if (m.activo) {
                                            e.currentTarget.style.transform = 'translateY(-5px)';
                                            e.currentTarget.style.boxShadow = '0 10px 25px rgba(0,0,0,0.15)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.transform = 'translateY(0)';
                                        e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.1)';
                                    }}
                                >
                                    <div 
                                        style={{
                                            background: m.activo 
                                                ? 'var(--primary)' 
                                                : 'var(--text-muted)',
                                            padding: '1.5rem',
                                            borderRadius: '12px 12px 0 0',
                                            color: 'white'
                                        }}
                                    >
                                        <div className="d-flex justify-content-between align-items-start">
                                            <div>
                                                <div style={{ fontSize: '0.75rem', opacity: 0.9, marginBottom: '0.3rem' }}>PROFESIONAL</div>
                                                <h5 className="mb-0" style={{ fontWeight: '700', fontSize: '1.2rem' }}>
                                                    <i className="bi bi-person-circle me-2"></i>
                                                    Dr/a. {m.nombre} {m.apellido}
                                                </h5>
                                            </div>
                                            {!m.activo && <Badge bg="dark" style={{ fontSize: '0.75rem' }}>Inactivo</Badge>}
                                        </div>
                                    </div>
                                    <Card.Body style={{ padding: '1.5rem' }}>
                                        <div className="mb-3">
                                            <div className="d-flex align-items-center mb-2">
                                                <i className="bi bi-award-fill text-primary me-2" style={{ fontSize: '1.1rem' }}></i>
                                                <strong style={{ fontSize: '1.05rem', color: '#3B82F6' }}>{m.especialidad || "Sin especialidad"}</strong>
                                            </div>
                                            <div className="d-flex align-items-center text-muted mb-1" style={{ fontSize: '0.9rem' }}>
                                                <i className="bi bi-card-text me-2"></i>
                                                <span><strong>Matrícula:</strong> {m.matricula || "—"}</span>
                                            </div>
                                            <div className="d-flex align-items-center text-muted" style={{ fontSize: '0.9rem' }}>
                                                <i className="bi bi-telephone-fill me-2"></i>
                                                <span><strong>Teléfono:</strong> {m.telefono || "—"}</span>
                                            </div>
                                        </div>
                                        
                                        <div 
                                            className="p-3 mb-3" 
                                            style={{ 
                                                background: 'var(--gradient-primary-light)',
                                                borderRadius: '8px',
                                                border: '1px solid #bae6fd'
                                            }}
                                        >
                                            <Row className="text-center">
                                                <Col xs={6}>
                                                    <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>PACIENTES</div>
                                                    <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0891b2' }}>
                                                        {m.consultas.length}
                                                    </div>
                                                </Col>
                                                <Col xs={6}>
                                                    <div style={{ fontSize: '0.75rem', color: '#6B7280', marginBottom: '0.25rem' }}>TOTAL GASTO</div>
                                                    <div style={{ fontSize: '1.2rem', fontWeight: '700', color: '#059669' }}>
                                                        ${total.toLocaleString()}
                                                    </div>
                                                </Col>
                                            </Row>
                                        </div>

                                        <div className="d-flex gap-2">
                                            {m.activo ? (
                                                <>
                                                    <Button 
                                                        variant="primary" 
                                                        size="sm" 
                                                        onClick={() => abrirModalDetalle(m)}
                                                        className="flex-grow-1"
                                                        style={{ borderRadius: '6px', fontWeight: '500' }}
                                                    >
                                                        <i className="bi bi-eye me-1"></i>Ver detalle
                                                    </Button>
                                                    <Button
                                                        variant="outline-danger"
                                                        size="sm"
                                                        onClick={() => abrirModalConfirmacion(m, "baja")}
                                                        style={{ borderRadius: '6px', fontWeight: '500' }}
                                                    >
                                                        <i className="bi bi-x-circle"></i>
                                                    </Button>
                                                </>
                                            ) : (
                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={() => abrirModalConfirmacion(m, "reactivar")}
                                                    className="w-100"
                                                    style={{ borderRadius: '6px', fontWeight: '500' }}
                                                >
                                                    Reactivar
                                                </Button>
                                            )}
                                        </div>
                                    </Card.Body>
                                </Card>
                            </Col>
                        );
                    })}
                </Row>
            )}

            {/* ModalDetalleMedico (MUI Dialog) */}
            <ModalDetalleMedico
                open={showDetalle}
                onClose={cerrarModalDetalle}
                medicoSeleccionado={medicoSeleccionado}
                onEditarClick={abrirModalEditar} // Usa la función coordinadora
            />

            {/* ModalEditarMedico (React Bootstrap Modal) */}
            <ModalEditarMedico
                show={showEdit}
                onHide={cerrarModalEditar}
                medico={medicoAEditar}
                onMedicoActualizado={() => {
                    // Actualizar en el modal de detalles si estaba abierto (opcional, ya que el detalle se cerró)
                    // Si se decide reabrir el modal de detalle después de editar:
                    // setMedicoSeleccionado(prev => ({ ...prev, ...medicoActualizado }));
                    // setShowDetalle(true);

                    cargarMedicos(filtro);
                }}
            />

            {/* Modal Registrar */}
            <Modal
                show={showForm}
                onHide={cerrarModalForm}
                centered
                backdrop="static"
                keyboard={false}
                size="lg"
            >
                <Modal.Header closeButton style={{ background: 'var(--primary)', color: 'white', border: 'none' }}>
                    <Modal.Title style={{ fontWeight: '600' }}>
                        <i className="bi bi-person-plus-fill me-2"></i>Registrar nuevo médico
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '2rem' }}>
                    <Form>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label >
                                        <i className="bi bi-person me-2 text-primary"></i>Nombre
                                    </Form.Label>
                                    <Form.Control 
                                        value={nuevoMedico.nombre} 
                                        onChange={(e) => setNuevoMedico({ ...nuevoMedico, nombre: e.target.value })}
                                        style={{ padding: '0.6rem' }}
                                        placeholder="Ingrese el nombre"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label >
                                        <i className="bi bi-person me-2 text-primary"></i>Apellido
                                    </Form.Label>
                                    <Form.Control 
                                        value={nuevoMedico.apellido} 
                                        onChange={(e) => setNuevoMedico({ ...nuevoMedico, apellido: e.target.value })}
                                        style={{ padding: '0.6rem' }}
                                        placeholder="Ingrese el apellido"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label >
                                        <i className="bi bi-card-text me-2 text-primary"></i>Matrícula
                                    </Form.Label>
                                    <Form.Control 
                                        value={nuevoMedico.matricula} 
                                        onChange={(e) => setNuevoMedico({ ...nuevoMedico, matricula: e.target.value })}
                                        style={{ padding: '0.6rem' }}
                                        placeholder="Ingrese la matrícula"
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label >
                                        <i className="bi bi-award-fill me-2 text-primary"></i>Especialidad
                                    </Form.Label>
                                    <Form.Control 
                                        value={nuevoMedico.especialidad} 
                                        onChange={(e) => setNuevoMedico({ ...nuevoMedico, especialidad: e.target.value })}
                                        style={{ padding: '0.6rem' }}
                                        placeholder="Ej: Traumatología"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>
                        <Form.Group className="mb-3">
                            <Form.Label >
                                <i className="bi bi-telephone-fill me-2 text-primary"></i>Teléfono
                            </Form.Label>
                            <Form.Control
                                type="tel"
                                value={nuevoMedico.telefono}
                                onChange={(e) => setNuevoMedico({ ...nuevoMedico, telefono: e.target.value })}
                                placeholder="Ej: 1123456789"
                                style={{ padding: '0.6rem' }}
                            />
                            <Form.Text className="text-muted">
                                <i className="bi bi-info-circle me-1"></i>
                                Ingrese solo números (8 a 15 dígitos)
                            </Form.Text>
                        </Form.Group>
                    </Form>
                </Modal.Body>
                <Modal.Footer style={{ border: 'none', padding: '1rem 2rem' }}>
                    <Button 
                        variant="outline-secondary" 
                        onClick={cerrarModalForm}
                        style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                    >
                        <i className="bi bi-x-circle me-2"></i>Cancelar
                    </Button>
                    <Button 
                        variant="success" 
                        onClick={handleCrearMedico}
                        style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                    >
                        <i className="bi bi-check-circle me-2"></i>Guardar
                    </Button>
                </Modal.Footer>
            </Modal>

            {/* Modal Confirmacion */}
            <Modal show={showConfirm} onHide={() => setShowConfirm(false)} centered>
                <Modal.Header 
                    closeButton 
                    style={{ 
                        background: accionConfirm === "baja" 
                            ? 'var(--gradient-danger)' 
                            : 'var(--gradient-success)',
                        color: 'white',
                        border: 'none'
                    }}
                >
                    <Modal.Title style={{ fontWeight: '600' }}>
                        <i className={`bi bi-${accionConfirm === "baja" ? 'exclamation-triangle' : 'check-circle'}-fill me-2`}></i>
                        {accionConfirm === "baja" ? "Dar de baja médico" : "Reactivar médico"}
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body style={{ padding: '2rem' }}>
                    <div className="text-center">
                        <i 
                            className={`bi bi-${accionConfirm === "baja" ? 'person-x' : 'person-check'}-fill`}
                            style={{ 
                                fontSize: '3rem', 
                                color: accionConfirm === "baja" ? '#ef4444' : '#10b981',
                                marginBottom: '1rem'
                            }}
                        ></i>
                        <p style={{ fontSize: '1.1rem', marginBottom: '0.5rem' }}>
                            ¿Deseas {accionConfirm === "baja" ? "dar de baja" : "reactivar"} al médico
                        </p>
                        <p style={{ fontSize: '1.3rem', fontWeight: '600', color: '#111827' }}>
                            Dr/a. {medicoAConfirmar?.nombre} {medicoAConfirmar?.apellido}
                        </p>
                        <p className="text-muted" style={{ fontSize: '0.9rem' }}>
                            {accionConfirm === "baja" 
                                ? "El médico quedará inactivo y no aparecerá en los listados principales."
                                : "El médico volverá a estar disponible en el sistema."}
                        </p>
                    </div>
                </Modal.Body>
                <Modal.Footer style={{ border: 'none', padding: '1rem 2rem' }}>
                    <Button 
                        variant="outline-secondary" 
                        onClick={() => setShowConfirm(false)}
                        style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                    >
                        <i className="bi bi-x-circle me-2"></i>Cancelar
                    </Button>
                    <Button
                        variant={accionConfirm === "baja" ? "danger" : "success"}
                        onClick={confirmarAccion}
                        style={{ borderRadius: '8px', padding: '0.6rem 1.5rem', fontWeight: '500' }}
                    >
                        <i className={`bi bi-${accionConfirm === "baja" ? 'trash' : 'arrow-clockwise'} me-2`}></i>
                        Confirmar
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    </div>
);
}

export default ListadoMedicos;
